import React, { useEffect, useState } from "react";
import { Download, X, Share, PlusSquare } from "lucide-react";

const DISMISS_KEY = "chemlab_install_dismissed";
const isStandalone = () => window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
const isIos = () => /iphone|ipad|ipod/i.test(navigator.userAgent) && !window.MSStream;
const isMobile = () => /android|iphone|ipad|ipod/i.test(navigator.userAgent);

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [iosHelp, setIosHelp] = useState(false);

  useEffect(() => {
    if (isStandalone() || localStorage.getItem(DISMISS_KEY) || !isMobile()) return undefined;
    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); setVisible(true); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    const t = setTimeout(() => { if (isIos()) setVisible(true); }, 4000);
    const onInstalled = () => { localStorage.setItem(DISMISS_KEY, "installed"); setVisible(false); };
    window.addEventListener("appinstalled", onInstalled);
    return () => { window.removeEventListener("beforeinstallprompt", onPrompt); window.removeEventListener("appinstalled", onInstalled); clearTimeout(t); };
  }, []);

  const dismiss = () => { localStorage.setItem(DISMISS_KEY, new Date().toISOString()); setVisible(false); };
  const install = async () => {
    if (!deferred) return setIosHelp(true);
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    if (outcome === "accepted") localStorage.setItem(DISMISS_KEY, "installed");
    setVisible(false);
  };

  if (!visible) return null;
  return (
    <div data-testid="install-prompt" className="fixed inset-x-3 bottom-[calc(env(safe-area-inset-bottom)+5rem)] z-40 rounded-2xl border border-cyan-400/30 bg-[#0b0f14]/95 p-4 shadow-2xl backdrop-blur-xl lg:hidden">
      <button onClick={dismiss} data-testid="install-dismiss" aria-label="Dismiss" className="icon-btn absolute right-2 top-2 rounded-full p-1.5 text-slate-400 hover:bg-white/10"><X size={14} /></button>
      <div className="flex items-start gap-3">
        <img src="/icons/icon-192.png" alt="" className="h-12 w-12 shrink-0 rounded-xl" />
        <div className="min-w-0 flex-1">
          <div className="font-display text-base font-semibold text-white">Add ChemLab to your home screen</div>
          <p className="mt-0.5 text-xs text-slate-400">Opens full-screen like an app, and your saved lessons work without signal.</p>
          {iosHelp ? (
            <ol className="mt-2 space-y-1 text-xs text-slate-300" data-testid="install-ios-steps">
              <li className="flex items-center gap-2"><Share size={12} className="text-cyan-300" /> 1. Tap the <b>Share</b> button in Safari</li>
              <li className="flex items-center gap-2"><PlusSquare size={12} className="text-cyan-300" /> 2. Choose <b>Add to Home Screen</b></li>
            </ol>
          ) : (
            <div className="mt-3 flex gap-2">
              <button onClick={install} data-testid="install-btn" className="inline-flex items-center gap-2 rounded-md bg-cyan-500 px-4 py-2 text-sm font-semibold text-black hover:bg-cyan-400"><Download size={14} /> Install</button>
              <button onClick={dismiss} data-testid="install-later" className="rounded-md px-3 py-2 text-sm text-slate-400 hover:text-slate-200">Not now</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
